/**
 * IPFS Service Worker Client
 *
 * Main thread helper for communicating with the IPFS SW.
 * After blockstore unification, the SW only handles PING and SET_CONFIG.
 * Block operations go directly through the shared IDB blockstore.
 */

import { Logger } from 'besonders-logger'
import type { CID } from 'multiformats/cid'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG, { prefix: '[ipfs-sw-client]' })

// Types
interface SWConfig {
	gateways: string[]
	ipnsResolvers: string[]
	ipnsGatewayResolvers: string[]
}

type SWResponse =
	| { type: 'PONG'; ready: boolean }
	| { type: 'CONFIG_UPDATED'; config: SWConfig }
	| { type: 'ERROR'; error: string }

// ============================================================================
// SW Communication (uses the unified PWA service worker)
// ============================================================================

let messageListenerAttached = false
let pendingMessages: Map<number, { resolve: (value: any) => void; reject: (err: Error) => void }> = new Map()
let messageIdCounter = 0

/**
 * Get the active service worker (the unified PWA SW registered by VitePWA)
 */
export async function getIpfsSW(): Promise<ServiceWorker> {
	if (!('serviceWorker' in navigator)) {
		throw new Error('Service workers not supported')
	}

	// Attach message listener once
	if (!messageListenerAttached) {
		navigator.serviceWorker.addEventListener('message', handleSWMessage)
		messageListenerAttached = true
	}

	const reg = await Promise.race([
		navigator.serviceWorker.ready,
		new Promise<never>((_, reject) =>
			setTimeout(() => reject(new Error('Service worker not ready (timeout — not registered?)')), 5000),
		),
	])
	const sw = reg.active

	if (!sw) {
		throw new Error('No active service worker')
	}

	return sw
}

/**
 * Check if IPFS SW is registered and ready
 */
export async function isIpfsSWReady(): Promise<boolean> {
	try {
		const response = await sendToSW({ type: 'PING' })
		return response.type === 'PONG' && response.ready
	} catch {
		return false
	}
}

// ============================================================================
// Message Handling
// ============================================================================

function handleSWMessage(event: MessageEvent) {
	const data = event.data as SWResponse & { _messageId?: number }

	// Check if this is a response to a pending message
	if (data._messageId !== undefined) {
		const pending = pendingMessages.get(data._messageId)
		if (pending) {
			pendingMessages.delete(data._messageId)
			if (data.type === 'ERROR') {
				pending.reject(new Error(data.error))
			} else {
				pending.resolve(data)
			}
		}
	}
}

/**
 * Send a message to the SW and wait for response
 */
async function sendToSW<T = SWResponse>(message: any, transfer?: Transferable[]): Promise<T> {
	const sw = await getIpfsSW()

	const messageId = messageIdCounter++
	const messageWithId = { ...message, _messageId: messageId }

	return new Promise((resolve, reject) => {
		// Set timeout
		const timeout = setTimeout(() => {
			pendingMessages.delete(messageId)
			reject(new Error(`SW message timeout: ${message.type}`))
		}, 30000) // 30s timeout

		pendingMessages.set(messageId, {
			resolve: (value) => {
				clearTimeout(timeout)
				resolve(value)
			},
			reject: (err) => {
				clearTimeout(timeout)
				reject(err)
			},
		})

		if (transfer?.length) {
			sw.postMessage(messageWithId, transfer)
		} else {
			sw.postMessage(messageWithId)
		}
	})
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Update SW configuration (gateways, resolvers, etc.)
 */
export async function setConfig(config: Partial<SWConfig>): Promise<void> {
	await sendToSW({ type: 'SET_CONFIG', config })
	LOG('Config sent to SW:', config)
}

// ============================================================================
// Utility: IPFS URL helpers
// ============================================================================

/**
 * Create an IPFS path URL that will be intercepted by the SW
 */
export function ipfsUrl(cid: CID | string, path = ''): string {
	const cidStr = typeof cid === 'string' ? cid : cid.toString()
	return `/ipfs/${cidStr}${path}`
}

/**
 * Create an IPNS path URL that will be intercepted by the SW
 */
export function ipnsUrl(name: string, path = ''): string {
	return `/ipns/${name}${path}`
}
